import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ArrowLeft, Heart, X, Loader2 } from "lucide-react";
import apiClient from "../../../api/apiClient";
import TopNavBar from "../../../components/TopNavBar";
import tablerBone from "../../../assets/tabler_bone.png";
import { ENDPOINTS } from "../../../api/endPoints";
import { useGlobalAlert } from "../../../context/AlertContext";
import { useNotifications } from "../../../context/NotificationContext";
import dogPlaceholder from "../../../assets/dogPlaceholder.png";
import userPlaceholder from "../../../assets/userPlaceholder.png";
import appIcon from "../../../assets/appIcon.svg";
import "./ReceiveATreatPage.css";
import MobileTopBar from "../../../components/MobileTopBar";

const ReceiveATreatPage = () => {
  const navigate = useNavigate();
  const { setUnreadCount } = useNotifications();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { triggerAlert } = useGlobalAlert();
  const { profile } = useSelector((state) => state.user);

  const [loading, setLoading] = useState(true);
  const [treatData, setTreatData] = useState(null);
  const [showTreatModal, setShowTreatModal] = useState(false);
  const [treatDog, setTreatDog] = useState(null);
  const [isTossingTreat, setIsTossingTreat] = useState(false);

  const [activeNotificationId, setActiveNotificationId] = useState(null);

  useEffect(() => {
    // Grab the ID from the Email URL or internal routing state
    const urlId = searchParams.get("notificationId");
    const stateId = location.state?.notificationId;
    const finalId = urlId || stateId;

    if (finalId) {
      setActiveNotificationId(finalId);

      // Clean up the URL/history so refreshing doesn't cause sticky behavior
      if (urlId) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      } else if (stateId) {
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [
    searchParams,
    location.state?.notificationId,
    location.pathname,
    navigate,
  ]);

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get(`/notifications/${profile?.id}`);
      if (response.data.status) {
        const data = response.data.data;
        const count = data.filter((n) => !n.is_read).length;
        setUnreadCount(count);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    if (profile?.id) {
      fetchNotifications();
    }
  }, [profile?.id]);

  useEffect(() => {
    const fetchTreatDetails = async () => {
      if (!activeNotificationId) return;

      setLoading(true);
      setTreatData(null);
      setShowTreatModal(false);

      try {
        const response = await apiClient.get(
          `/notifications/get-notification-ById-for-treat/${activeNotificationId}`,
        );
        if (response.data.status && response.data.data.length > 0) {
          setTreatData(response.data.data[0]);
        }
      } catch (error) {
        console.error("Error fetching treat details:", error);
        triggerAlert("Could not load treat details", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchTreatDetails();
  }, [activeNotificationId, triggerAlert]);

  const isSender = profile?.id === treatData?.sender_user_id;

  const partnerDogName = isSender
    ? treatData?.receiver_dog_name || treatData?.receiver_user_name
    : treatData?.sender_dog_name && treatData?.sender_dog_name.trim() !== ""
      ? treatData?.sender_dog_name
      : treatData?.sender_user_name;

  const partnerImage = isSender
    ? treatData?.receiver_dog_image && treatData?.receiver_dog_image.length > 0
      ? treatData?.receiver_dog_image[0]
      : treatData?.receiver_profile_image?.[0]
    : treatData?.sender_dog_image && treatData?.sender_dog_image.length > 0
      ? treatData?.sender_dog_image[0]
      : treatData?.sender_profile_image?.[0];

  const partnerName = isSender
    ? treatData?.receiver_user_name
    : treatData?.sender_user_name;

  const myDogName = isSender
    ? treatData?.sender_dog_name || profile?.user_name
    : treatData?.receiver_dog_name || profile?.user_name;

  const [showIncompleteProfileModal, setShowIncompleteProfileModal] =
    useState(false);

  // CHECK COMPLETION PROFILE REDIRECT LOGIC
  const checkCompletionStatus = () => {
    const isComplete = profile?.dogs?.[0]?.isDogProfileComplete;
    const isHalfComplete = profile?.dogs?.[0]?.isDogProfileHalfComplete;

    // If fully complete, allow the action
    if (isComplete === 1) {
      return true;
    }

    // If half complete, redirect to half-completed page
    if (isComplete === 0 && isHalfComplete === 1) {
      navigate("/half-completed-page");
      return false;
    }

    // Fallback/Default: Trigger the incomplete profile popup instead of instantly navigating
    setShowIncompleteProfileModal(true);
    return false;
  };

  const handleTossTreat = async () => {
    if (!checkCompletionStatus()) return;
    if (!profile?.id || !treatData) return;

    setIsTossingTreat(true);

    try {
      const payload = {
        sender_user_id: profile?.id,
        receiver_user_id: isSender
          ? treatData?.receiver_user_id
          : treatData?.sender_user_id,
        receiver_dog_id: isSender
          ? treatData?.receiver_dog_id
          : treatData?.sender_dog_id,
      };

      const response = await apiClient.post(
        ENDPOINTS.CREATE_WEBAPP_TREAT,
        payload,
      );

      if (response.data?.status) {
        setTreatDog({
          dog_name: partnerDogName,
          profile_image: [partnerImage],
        });
        setShowTreatModal(true);
      } else {
        triggerAlert(
          response.data?.message || "Failed to toss a treat.",
          "error",
        );
      }
    } catch (error) {
      console.error("Error tossing treat:", error);

      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          `Server Error: ${error.response.status}`;
        triggerAlert(errorMessage, "error");
      } else if (error.request) {
        triggerAlert(
          "No response from server. Please check your internet connection.",
          "error",
        );
      } else {
        triggerAlert("An unexpected error occurred.", "error");
      }
    } finally {
      setIsTossingTreat(false);
    }
  };

  const handleCloseModal = () => {
    setShowTreatModal(false);
    setTreatDog(null);
  };

  const handleGotoMessage = () => {
    navigate("/messages", {
      state: { partnerId: treatData?.sender_user_id },
    });
  };

  const handleUpdateAvailability = () => {
    const dogStatus =
      profile?.user_dog_status ??
      Number(localStorage.getItem("user_dog_status"));
    const route =
      dogStatus === 1 ? "/team-paws-availability" : "/team-social-availability";

    const partnerUserId = isSender
      ? treatData?.receiver_user_id
      : treatData?.sender_user_id;

    navigate(route, {
      state: {
        partnerId: partnerUserId,
        partnerName: partnerName,
        myDogName: myDogName,
      },
    });
  };

  const handleImgError = (e) => {
    e.target.src = dogPlaceholder;
  };

  if (loading) {
    return (
      <div className="rat-loading-screen">
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }
  if (!treatData)
    return <div className="text-center">No treat data found.</div>;

  return (
    <div className="rat-container">
      {/* DESKTOP HEADER */}
      <div
        className="msg-custom-top-header notif-desktop-only"
        style={{ padding: "2rem" }}
      >
        <div
          className="notif-ui-navbar-div"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
            marginTop: "0",
          }}
          onClick={() => navigate("/dashboard-switcher")}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "32px", height: "32px", objectFit: "contain" }}
          />
          <span
            style={{
              color: "#fff",
              fontSize: "22px",
              fontWeight: "700",
            }}
          >
            Social Paws
          </span>
        </div>

        <div className="notif-ui-nav-icons">
          <TopNavBar theme="dark" />
        </div>
      </div>

      <div className="notif-ui-navbar-mobile notif-mobile-only">
        <MobileTopBar
          onBack={() => navigate(-1)}
          backColor="#fff"
          logoColor="#fff"
        />

        <div className="notif-mobile-header-bottom">
          <div className="notif-ui-nav-icons">
            <TopNavBar theme="dark" />
          </div>
        </div>
      </div>

      {/* CENTER CONTENT */}
      <div className="rat-content-wrapper">
        <div className="rat-panel">
          <div className="rat-card">
            {/* DYNAMIC TOP TEXT */}
            <div className="rat-top-text">
              {isSender
                ? `You tossed ${partnerDogName || partnerName} a treat!`
                : `${myDogName || profile?.user_name} received a treat from ${partnerDogName}!`}
            </div>

            {/* IMAGE BLOCK */}
            <div className="rat-image-container">
              <img
                src={partnerImage || userPlaceholder}
                alt={partnerDogName}
                onError={handleImgError}
              />
              <div className="rat-image-overlay">
                <Heart size={30} color="#fff" strokeWidth={2} />
                <span>{partnerDogName}</span>
              </div>
            </div>

            {/* DYNAMIC BOTTOM TEXT */}
            <div className="rat-bottom-text">
              Take this opportunity to{" "}
              <span
                style={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={handleUpdateAvailability}
              >
                update {myDogName || "your pup's"} availability
              </span>
              , as Pup Pals may be requesting walks soon.
            </div>

            {/* BUTTONS */}
            <div className="rat-action-btns">
              {!isSender && (
                <button
                  className="rat-btn rat-btn-secondary"
                  style={{ width: "320px" }}
                  onClick={handleTossTreat}
                  disabled={isTossingTreat}
                >
                  {isTossingTreat
                    ? "TOSSING..."
                    : `TOSS A TREAT BACK TO ${partnerDogName?.toUpperCase()}`}
                </button>
              )}
              <button
                className="rat-btn rat-btn-primary"
                onClick={() => {
                  handleGotoMessage();
                }}
                style={{ width: isSender ? "400px" : "270px" }}
              >
                MESSAGE {partnerDogName?.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* INCOMPLETE PROFILE MODAL */}
      {showIncompleteProfileModal && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div
            className="book-modal-card"
            style={{
              padding: "1.5rem",
              textAlign: "center",
              width: window.innerWidth >= 1024 ? "400px" : "",
              height: window.innerWidth >= 1024 ? "180px" : "",
            }}
          >
            <button
              className="modal-close-btn"
              onClick={() => setShowIncompleteProfileModal(false)}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <h2
              className="book-title"
              style={{
                marginTop: "1rem",
                marginBottom: "1rem",
                fontSize: "1rem",
              }}
            >
              Want to interact with the community? Complete your profile first!
              🐾
            </h2>

            <div
              style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
            >
              <button
                className="profile-btn-half"
                onClick={() => {
                  setShowIncompleteProfileModal(false);
                  navigate("/team-selection", {
                    state: { routePath: "checkCompletionStatus" },
                  });
                }}
              >
                CONTINUE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TREAT CONFIRMATION MODAL */}
      {showTreatModal && treatDog && (
        <div className="treat-modal-overlay">
          <div className="treat-modal-card">
            <button className="treat-close-btn" onClick={handleCloseModal}>
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <div className="treat-modal-content">
              <div className="treat-icons-row">
                <div className="cb-treat-dog-avatar">
                  <img
                    src={partnerImage || dogPlaceholder}
                    alt={treatDog.dog_name}
                    onError={handleImgError}
                  />
                </div>

                <img
                  src={tablerBone}
                  alt="tablerBoneIcon"
                  style={{ width: "60px" }}
                />
              </div>

              <h2 className="treat-text-main">
                You just tossed {treatDog.dog_name} a treat!
              </h2>

              <p className="treat-text-sub">
                Treats are limited to one per day per dog
              </p>

              <h3 className="treat-high-paw">HIGH PAW!</h3>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiveATreatPage;
